import { CloudArrowUpIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import {
  getDocType,
  getDocTypeBadgeClass,
  type DocTypeBadgeClasses,
} from '../coding-guidelines/coding-guidelines-types.ts'
import { DocTypeIcon } from '../coding-guidelines/doc-type-icon.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './freeform-dropzone.css.ts'
import { FREEFORM_ACCEPTED_HINT, formatFileSize, type StagedFile } from './freeform-types.ts'

// Per-doc-type icon color classes, matching the coding-guidelines palette so both
// upload surfaces color file icons identically; unknown types fall back to `base`.
const CHIP_ICON_CLASSES: DocTypeBadgeClasses = {
  pdf: styles.chipIconPdf,
  docx: styles.chipIconDocx,
  md: styles.chipIconMd,
  txt: styles.chipIconTxt,
  html: styles.chipIconHtml,
  base: styles.chipIcon,
}

export type FreeformDropzoneProps = {
  stagedFiles: StagedFile[]
  onAddFiles: (files: File[]) => unknown
  onRemoveFile: (id: string) => unknown
  isDisabled?: boolean
  errorMessage?: string
}

export const FreeformDropzone: React.FC<FreeformDropzoneProps> = ({
  stagedFiles,
  onAddFiles,
  onRemoveFile,
  isDisabled = false,
  errorMessage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const openPicker = () => {
    if (!isDisabled) {
      fileInputRef.current?.click()
    }
  }

  const handleIncoming = (fileList: FileList | undefined) => {
    if (!fileList || fileList.length === 0) {
      return
    }
    onAddFiles(Array.from(fileList))
    // Reset so selecting the same file again still fires a change event.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className={styles.wrapper}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        disabled={isDisabled}
        onChange={e => handleIncoming(e.target.files ?? undefined)}
      />
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-label="Add files"
        className={classNames(
          styles.dropzone,
          isDragOver && !isDisabled && styles.dropzoneActive,
          Boolean(errorMessage) && styles.dropzoneErrorBorder,
          isDisabled && styles.dropzoneDisabled
        )}
        onClick={openPicker}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openPicker()
          }
        }}
        onDragOver={e => {
          e.preventDefault()
          if (!isDisabled) {
            setIsDragOver(true)
          }
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={e => {
          e.preventDefault()
          setIsDragOver(false)
          if (!isDisabled) {
            handleIncoming(e.dataTransfer.files)
          }
        }}
      >
        <CloudArrowUpIcon size={32} weight="light" />
        <p className={styles.dropzoneLabel}>
          Drop files here, or{' '}
          <button
            type="button"
            className={styles.browseLink}
            disabled={isDisabled}
            onClick={e => {
              e.stopPropagation()
              openPicker()
            }}
          >
            browse
          </button>
        </p>
        <p className={styles.dropzoneSub}>{FREEFORM_ACCEPTED_HINT}</p>
      </div>

      {errorMessage ? (
        <p className={styles.errorText} role="alert">
          <WarningCircleIcon size={14} weight="fill" />
          {errorMessage}
        </p>
      ) : null}

      {stagedFiles.length > 0 ? (
        <ul className={styles.chipList} aria-label="Selected files">
          {stagedFiles.map(({ id, file }) => {
            const docType = getDocType(file.name)
            return (
              <li key={id} className={styles.chip}>
                <span className={getDocTypeBadgeClass({ docType, classes: CHIP_ICON_CLASSES })} aria-hidden>
                  <DocTypeIcon docType={docType} size={16} />
                </span>
                <div className={styles.chipMeta}>
                  <p className={styles.chipName} title={file.name}>
                    {file.name}
                  </p>
                  <p className={styles.chipSize}>{formatFileSize(file.size)}</p>
                </div>
                <TooltipWithHover
                  trigger={
                    <button
                      type="button"
                      className={styles.chipRemove}
                      aria-label={`Remove ${file.name}`}
                      disabled={isDisabled}
                      onClick={() => onRemoveFile(id)}
                    >
                      <XIcon size={16} weight="light" />
                    </button>
                  }
                >
                  Remove
                </TooltipWithHover>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
